import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CircleDashed,
  Clock,
  MonitorSmartphone,
  ShieldCheck,
  TimerOff,
  UserX,
  Users
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useAppSelector } from '@/app/hooks'
import { selectDealerStats, selectRecentDealers } from '@/features/dealers/dealersSlice'
import { selectVisibleExceptions } from '@/features/exceptions/exceptionsSlice'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardHeader, EmptyState, Spinner } from '@/components/ui/Surface'
import { RoleBadge, StatusBadge } from '@/components/ui/Badge'
import { dealerRole, formatRelative, initialsOf } from '@/lib/format'
import { cn } from '@/lib/cn'

/** Rows each dashboard panel holds. The panel scrolls past what 500px shows. */
const DASHBOARD_ROWS = 20

interface StatProps {
  label: string
  value: number
  icon: LucideIcon
  /** Colour of the corner glyph — the one spot of hue on each tile. */
  tint: string
  hint?: string
  to?: string
}

/**
 * Modelled on the index chips across the top of the trading screens: uppercase
 * label, big tabular figure, a small glyph in the corner.
 */
function Stat({ label, value, icon: Icon, tint, hint, to }: StatProps): ReactNode {
  const tile = (
    <div
      className={cn(
        'h-full rounded-lg border border-line bg-surface px-3 py-3 transition-colors',
        to && 'hover:border-accent/50'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="label-caps text-muted">{label}</p>
        <Icon size={14} className={cn('shrink-0', tint)} aria-hidden />
      </div>
      <p className="mt-2 text-[20px] font-semibold leading-none tabular-nums tracking-tight text-fg">
        {value}
      </p>
      {hint && <p className="mt-1.5 text-[11px] text-subtle">{hint}</p>}
    </div>
  )
  return to ? (
    <Link to={to} className="block">
      {tile}
    </Link>
  ) : (
    tile
  )
}

function SeeAll({ to, label }: { to: string; label: string }): ReactNode {
  return (
    <Link
      to={to}
      className="label-caps flex items-center gap-1 text-accent transition-colors hover:text-accent/80"
    >
      {label} <ArrowRight size={12} aria-hidden />
    </Link>
  )
}

export function DashboardPage(): ReactNode {
  const stats = useAppSelector(selectDealerStats)
  const recent = useAppSelector(selectRecentDealers)
  const exceptions = useAppSelector(selectVisibleExceptions)
  const loading = useAppSelector((s) => s.dealers.loading && s.dealers.items.length === 0)
  const error = useAppSelector((s) => s.dealers.error)

  const last24h = exceptions.filter(
    (e) => new Date(e.created_at).getTime() >= Date.now() - 86_400_000
  ).length

  if (loading) {
    return (
      <>
        <PageHeader title="Dashboard" description="A read on the dealer base right now." />
        <Card>
          <Spinner label="Loading dealers" />
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader title="Dashboard" description="A read on the dealer base right now." />

      {error && (
        <Card className="mb-4 border-negative/40 bg-negative/10 px-4 py-3">
          <p className="flex items-center gap-2 text-[12.5px] text-negative">
            <AlertTriangle size={14} aria-hidden />
            {error}
          </p>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Accounts"
          value={stats.total}
          icon={Users}
          tint="text-accent"
          hint={stats.primaries + ' primary · ' + stats.subs + ' sub'}
          to="/dealers"
        />
        <Stat
          label="On a paid plan"
          value={stats.paid}
          icon={BadgeCheck}
          tint="text-positive"
          hint="Primary dealers billing"
          to="/dealers"
        />
        <Stat
          label="On trial"
          value={stats.trial}
          icon={Clock}
          tint="text-caution"
          hint="Converting soon"
          to="/dealers"
        />
        <Stat
          label="Awaiting activation"
          value={stats.pending}
          icon={CircleDashed}
          tint="text-info"
          hint="Registered, no plan yet"
          to="/dealers"
        />
        <Stat
          label="Expired"
          value={stats.expired}
          icon={TimerOff}
          tint="text-negative"
          hint="Needs renewal"
          to="/dealers"
        />
        <Stat
          label="Deactivated"
          value={stats.inactive}
          icon={UserX}
          tint="text-muted"
          hint="Blocked from signing in"
          to="/dealers"
        />
        <Stat
          label="Client seats"
          value={stats.seats}
          icon={MonitorSmartphone}
          tint="text-info"
          hint="Total allowed across accounts"
        />
        <Stat
          label="Exceptions 24h"
          value={last24h}
          icon={last24h > 0 ? AlertTriangle : ShieldCheck}
          tint={last24h > 0 ? 'text-negative' : 'text-positive'}
          hint="Reported by desktop clients"
          to="/exceptions"
        />
      </div>

      {/* Both panels cap at 500px and scroll their own rows, so a long list
          never pushes the rest of the dashboard off the screen. */}
      <div className="mt-3 grid gap-3 xl:grid-cols-2">
        <Card className="flex max-h-[310px] flex-col">
          <CardHeader
            title="Newest accounts"
            description="Most recent registrations"
            icon={Users}
            actions={<SeeAll to="/dealers" label="All dealers" />}
          />
          {recent.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No dealers yet"
              description="Accounts appear here as customers register in the desktop app."
            />
          ) : (
            <ul className="scroll-slim min-h-0 flex-1 divide-y divide-line overflow-y-auto">
              {recent.slice(0, DASHBOARD_ROWS).map((d) => (
                <li key={d.user_id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded border border-line bg-raised text-[10.5px] font-bold text-muted">
                    {initialsOf(d.name)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12.5px] font-medium text-fg">{d.name}</p>
                    <p className="truncate text-[11px] text-muted">{d.email}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <RoleBadge role={dealerRole(d)} />
                    <StatusBadge status={d.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="flex max-h-[500px] flex-col">
          <CardHeader
            title="Latest exceptions"
            description="Errors reported by client machines"
            icon={AlertTriangle}
            actions={<SeeAll to="/exceptions" label="All exceptions" />}
          />
          {exceptions.length === 0 ? (
            <EmptyState
              icon={BadgeCheck}
              title="Nothing reported"
              description="Client machines have not sent any errors."
            />
          ) : (
            <ul className="scroll-slim min-h-0 flex-1 divide-y divide-line overflow-y-auto">
              {exceptions.slice(0, DASHBOARD_ROWS).map((e) => (
                <li key={e.id} className="px-4 py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="truncate text-[12px] font-medium text-fg">
                      {e.user_email || 'unknown dealer'}
                    </p>
                    <span className="shrink-0 text-[10.5px] text-subtle">
                      {formatRelative(e.created_at)}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 font-mono text-[11px] leading-snug text-muted">
                    {e.exception}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  )
}
